import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { KeyboardKeys } from '@/shared/utils/navigation';

import { debounce, getNewOptionObject } from '@/utils/common';

export interface IOption {
	id: string;
	name: string;
}

type UseFieldOptionsParams = {
	options: string[];
	onChangeOptions?: (updatedOptions: string[]) => void;
	editMode?: boolean;
	isFocusedInput?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
};

const toOptionObjects = (options: string[]): IOption[] =>
	options.map((option) => ({ id: getNewOptionObject(option).id, name: option }));

// Shared option-list editing for select-type fields (dropdown / single / multi).
// Owns the local option copy, add/delete/reorder/edit/keyboard behavior, and the
// post-mutation auto-focus. Selection semantics (radio/checkbox/"other") stay per-field.
export function useFieldOptions({
	options,
	onChangeOptions,
	editMode,
	isFocusedInput,
	onFocus,
	onBlur,
}: UseFieldOptionsParams) {
	const [optionsCopy, setOptionsCopy] = useState<IOption[]>(() => toOptionObjects(options));

	const inputsOptionsRefMap = useRef<Record<string, HTMLInputElement | null>>({});
	const inputOptionIdToAutoFocusRef = useRef<string | null>(null);

	const displayDeleteButton = !!editMode && !!isFocusedInput && optionsCopy.length > 1;
	const displayReorderIcon = displayDeleteButton;
	const displayAddOption = !!editMode && !!isFocusedInput;

	const focusOnFirstOption = useCallback(() => {
		if (optionsCopy.length > 0) {
			inputsOptionsRefMap.current[optionsCopy[0]?.id]?.focus();
		}
	}, [optionsCopy]);

	const handleSetCacheState = useMemo(
		() =>
			debounce((value) => {
				onChangeOptions?.(value as string[]);
			}, 250),
		[onChangeOptions]
	);

	// Send a pending edit now rather than after the options are gone; a layout cleanup
	// runs before the form editor's passive cleanup drops its cache.
	useLayoutEffect(() => handleSetCacheState.flush, [handleSetCacheState]);

	const handleSetOptionsInAllStates = useCallback(
		(newOptions: IOption[]) => {
			setOptionsCopy(newOptions);
			handleSetCacheState(newOptions.map((option) => option.name));
		},
		[handleSetCacheState]
	);

	const handleOnReorderOptions = useCallback(
		(newOptions: IOption[]) => {
			handleSetOptionsInAllStates(newOptions);
		},
		[handleSetOptionsInAllStates]
	);

	const handleOnDeleteOption = useCallback(
		(optionId: string) => () => {
			let deletedOptionIndex = -1;

			const updatedOptions = optionsCopy.filter((option, index) => {
				if (option.id === optionId) {
					deletedOptionIndex = index;
					return false;
				}
				return true;
			});

			const optionBeforeDeletedOption = updatedOptions[deletedOptionIndex - 1];
			const optionAfterDeletedOption = updatedOptions[deletedOptionIndex + 1];
			const optionAtDeletedOptionIndex = updatedOptions[deletedOptionIndex];

			inputOptionIdToAutoFocusRef.current =
				optionBeforeDeletedOption?.id ??
				optionAtDeletedOptionIndex?.id ??
				optionAfterDeletedOption?.id ??
				null;

			handleSetOptionsInAllStates(updatedOptions);
		},
		[optionsCopy, handleSetOptionsInAllStates]
	);

	const onClickAddOption = useCallback(
		(optionId?: string) => () => {
			const currLength = optionsCopy.length;
			const newOptionObj = getNewOptionObject(`Option ${currLength + 1}`);
			let updatedOptions = [...optionsCopy];

			if (optionId) {
				const addNextToIndex = optionsCopy.findIndex((option) => option.id === optionId);
				updatedOptions.splice(addNextToIndex + 1, 0, newOptionObj);
			} else {
				updatedOptions = [...optionsCopy, newOptionObj];
			}

			inputOptionIdToAutoFocusRef.current = newOptionObj.id;

			handleSetOptionsInAllStates(updatedOptions);
		},
		[optionsCopy, handleSetOptionsInAllStates]
	);

	const handleInputOptionChange = useCallback(
		(optionId: string) => (updatedOptionName: string) => {
			const updatedOptions = optionsCopy.map((option) =>
				option.id === optionId ? { ...option, name: updatedOptionName?.trim() } : option
			);
			handleSetOptionsInAllStates(updatedOptions);
		},
		[optionsCopy, handleSetOptionsInAllStates]
	);

	const handleInputOptionFocus = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			onFocus?.();
			e.target.select();
		},
		[onFocus]
	);

	const handleInputOptionBlur = useCallback(() => {
		onBlur?.();
	}, [onBlur]);

	const handleInputOptionKeyDown = useCallback(
		(optionId: string) => (e: React.KeyboardEvent<HTMLInputElement>) => {
			const key = e.key;
			const targetValue = e.currentTarget.value;
			const isLastOption = optionsCopy[optionsCopy.length - 1]?.id === optionId;

			if (
				(key === KeyboardKeys.Enter || key === KeyboardKeys.Tab) &&
				displayAddOption &&
				isLastOption
			) {
				e.preventDefault();
				onClickAddOption(optionId)();
			} else if (key === KeyboardKeys.Enter || key === KeyboardKeys.Tab) {
				const currentOptionIndex = optionsCopy.findIndex((option) => option.id === optionId);
				const nextOptionId = optionsCopy[currentOptionIndex + 1]?.id;
				const inputElement = inputsOptionsRefMap.current[nextOptionId];
				if (inputElement) {
					inputElement.focus();
					e.preventDefault();
					e.stopPropagation();
				}
			}

			if (key === KeyboardKeys.Backspace && !targetValue && displayDeleteButton) {
				e.preventDefault();
				handleOnDeleteOption(optionId)();
			}
			if (key === KeyboardKeys.Escape) {
				(e.target as HTMLInputElement)?.blur();
			}
		},
		[optionsCopy, displayAddOption, displayDeleteButton, onClickAddOption, handleOnDeleteOption]
	);

	const registerOptionRef = useCallback(
		(optionId: string) => (el: HTMLInputElement | null) => {
			inputsOptionsRefMap.current[optionId] = el;
		},
		[]
	);

	useEffect(() => {
		if (!inputOptionIdToAutoFocusRef.current) return;

		const targetId = inputOptionIdToAutoFocusRef.current;

		const observer = new MutationObserver((_mutations, obs) => {
			const inputElement = inputsOptionsRefMap.current[targetId];
			if (inputElement) {
				inputElement.focus();
				inputElement.select();
				inputOptionIdToAutoFocusRef.current = null;
				obs.disconnect();
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, [optionsCopy]);

	return {
		optionsCopy,
		displayDeleteButton,
		displayReorderIcon,
		displayAddOption,
		focusOnFirstOption,
		registerOptionRef,
		handleOnReorderOptions,
		handleOnDeleteOption,
		onClickAddOption,
		handleInputOptionChange,
		handleInputOptionFocus,
		handleInputOptionBlur,
		handleInputOptionKeyDown,
	};
}
